import clsx from "clsx";
import type { Hero, HeroClass } from "../types";
import { PlayerNameBadges } from "./PlayerNameBadges";

const CLASS_ICONS: Record<string, string> = {
  WARRIOR: "⚔️",
  ARCHER: "🏹",
  MAGE: "🔮",
};

const CLASS_COLORS: Record<string, string> = {
  WARRIOR: "border-orange-500 shadow-orange-500/20",
  ARCHER: "border-green-500 shadow-green-500/20",
  MAGE: "border-purple-500 shadow-purple-500/20",
};

function powerLabel(heroClass: HeroClass): string {
  return heroClass === "MAGE" ? "Intellect" : "Power";
}

function mainAttackLabel(heroClass: HeroClass): string {
  return heroClass === "MAGE" ? "MATK" : "PATK";
}

function mainAttackValue(hero: Hero): number {
  return hero.hero_class === "MAGE" ? hero.stats.matk : hero.stats.patk;
}

interface HeroCardProps {
  hero: Hero;
  compact?: boolean;
}

export function HeroCard({ hero, compact = false }: HeroCardProps) {
  const isMaxLevel = hero.xp_for_next_level === 0;
  const progressPct = isMaxLevel
    ? 100
    : Math.min(100, Math.round((hero.xp_into_level / hero.xp_for_next_level) * 100));

  return (
    <div
      className={clsx(
        "bg-kingdom-purple rounded-xl border-2 shadow-lg",
        CLASS_COLORS[hero.hero_class],
        compact ? "p-3" : "p-4",
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-3xl">{CLASS_ICONS[hero.hero_class]}</span>
          <div className="min-w-0">
            <PlayerNameBadges
              name={hero.name}
              guildName={hero.guild_name}
              vipSubscriptionActive={hero.premium?.vip.subscription_active}
              proActive={hero.premium?.pro.contract_active}
              size="sm"
            />
            <div className="text-gray-400 text-xs">{hero.hero_class} · {hero.formation}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-gold-400 font-bold text-xl">Lv.{hero.level}</div>
          <div className="text-gray-400 text-xs">
            {isMaxLevel ? "MAX LEVEL" : `${hero.xp_into_level}/${hero.xp_for_next_level} XP`}
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div className="h-2.5 bg-kingdom-dark rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-purple-500 to-kingdom-glow transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        {!isMaxLevel && !compact && (
          <div className="text-[10px] text-gray-500 mt-1 text-right">
            {hero.xp_to_next} XP to Lv.{hero.level + 1}
          </div>
        )}
      </div>

      {!compact && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <StatBadge label={powerLabel(hero.hero_class)} value={hero.stats.power} icon="💪" />
          <StatBadge label="Armor" value={hero.stats.armor} icon="🛡️" />
          <StatBadge label="Endurance" value={hero.stats.endurance} icon="🫀" />
          <StatBadge label="CRIT" value={hero.stats.crit} icon="💥" />
          <StatBadge label="HP" value={`${hero.battle_hp}/${hero.stats.hp}`} icon="❤️" />
          <StatBadge label="Mana" value={`${hero.battle_mana}/${hero.max_mana}`} icon="💧" />
          <StatBadge
            label={mainAttackLabel(hero.hero_class)}
            value={mainAttackValue(hero)}
            icon={hero.hero_class === "MAGE" ? "✨" : "⚔️"}
          />
          <StatBadge label="PDEF" value={hero.stats.pdef} icon="🔰" />
          <StatBadge label="MDEF" value={hero.stats.mdef} icon="🔮" />
        </div>
      )}

      {!compact && (
        <div className="mb-3">
          <div className="text-xs text-gray-400 mb-1">Skill Rotation</div>
          <div className="flex gap-1 flex-wrap">
            {hero.rotation.map((skill, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 bg-kingdom-mid rounded border border-kingdom-light text-kingdom-glow"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatBadge({ label, value, icon }: { label: string; value: number | string; icon: string }) {
  return (
    <div className="flex items-center gap-1.5 bg-kingdom-dark rounded-lg px-2 py-1.5">
      <span className="text-sm">{icon}</span>
      <div>
        <div className="text-xs text-gray-400">{label}</div>
        <div className="text-white font-bold text-sm">{value}</div>
      </div>
    </div>
  );
}
